// La connessione «API» (tela, artboard 51): un servizio HTTP che i pop leggono in GET e, se le
// accendi, scrivono in POST, PUT, PATCH e DELETE. Indirizzo
// base, autenticazione a scelta (nessuna, Bearer, chiave in un'intestazione o nell'indirizzo,
// utente e password) e limite della risposta. Il segreto resta sul server di PopTown.
import { Field, TextField } from '../../components/Field';
import { Select } from '../../components/Select';

export type ApiAuth = {
  mode: 'none' | 'bearer' | 'header' | 'query' | 'basic';
  name: string;
  user: string;
};

export const NO_AUTH: ApiAuth = { mode: 'none', name: '', user: '' };

const MODES: { value: ApiAuth['mode']; label: string }[] = [
  { value: 'none', label: 'Nessuna (API pubblica)' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'header', label: 'Chiave in un’intestazione' },
  { value: 'query', label: 'Chiave nell’indirizzo' },
  { value: 'basic', label: 'Utente e password' },
];

const SIZES = [100_000, 500_000, 1_000_000, 5_000_000, 10_000_000];
const size = (n: number) => (n < 1_000_000 ? `${n / 1000} KB` : `${n / 1_000_000} MB`);

/** Il form è completo quando il server lo accetterebbe. */
export function apiReady(url: string, auth: ApiAuth, secret: string): boolean {
  if (!/^https?:\/\/\S+$/.test(url.trim())) return false;
  if (auth.mode === 'none') return true;
  if (!secret.trim()) return false;
  if ((auth.mode === 'header' || auth.mode === 'query') && !auth.name.trim()) return false;
  return auth.mode !== 'basic' || !!auth.user.trim();
}

export function ApiForm({
  url,
  onUrl,
  auth,
  onAuth,
  secret,
  onSecret,
  maxBytes,
  onMaxBytes,
}: {
  url: string;
  onUrl: (v: string) => void;
  auth: ApiAuth;
  onAuth: (v: ApiAuth) => void;
  secret: string;
  onSecret: (v: string) => void;
  maxBytes: number;
  onMaxBytes: (v: number) => void;
}) {
  const keyed = auth.mode === 'header' || auth.mode === 'query';
  return (
    <>
      <Field
        label="Indirizzo base"
        hint="Solo l’indirizzo pubblico, senza ?…: i parametri si descrivono sotto e li mette il pop a ogni chiamata."
      >
        <TextField value={url} onChange={onUrl} placeholder="https://api.open-meteo.com/v1/" mono />
      </Field>
      <div className="rw-grid">
        <Field label="Autenticazione">
          <Select
            label="Autenticazione"
            value={auth.mode}
            onChange={(mode) => onAuth({ ...auth, mode: mode as ApiAuth['mode'] })}
            options={MODES}
            width="100%"
          />
        </Field>
        <Field label="Risposta al massimo" hint="Oltre, PopTown la taglia e lo dice al pop.">
          <Select
            label="Risposta al massimo"
            value={String(maxBytes)}
            onChange={(v) => onMaxBytes(Number(v))}
            options={SIZES.map((n) => ({ value: String(n), label: size(n) }))}
            width="100%"
          />
        </Field>
      </div>
      {(keyed || auth.mode === 'basic') && (
        <div className="rw-grid">
          {keyed && (
            <Field label={auth.mode === 'header' ? 'Nome dell’intestazione' : 'Nome del parametro'}>
              <TextField
                value={auth.name}
                onChange={(name) => onAuth({ ...auth, name })}
                placeholder={auth.mode === 'header' ? 'X-Api-Key' : 'apikey'}
                mono
              />
            </Field>
          )}
          {auth.mode === 'basic' && (
            <Field label="Utente">
              <TextField value={auth.user} onChange={(user) => onAuth({ ...auth, user })} />
            </Field>
          )}
        </div>
      )}
      {auth.mode !== 'none' && (
        <Field
          label={auth.mode === 'bearer' ? 'Token' : auth.mode === 'basic' ? 'Password' : 'Chiave'}
          hint="Resta sul server di PopTown: non torna mai alla plancia."
        >
          <TextField value={secret} onChange={onSecret} type="password" autoComplete="off" mono />
        </Field>
      )}
    </>
  );
}

/** I metodi della connessione, come chip: di partenza solo GET (tela, «Aggiungi API»). */
export function MethodChips() {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span style={{ font: '800 15px/20px var(--font-ui)' }}>Metodi</span>
      <div className="method-chips">
        {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
          <span key={m} data-on={m === 'GET' || undefined}>
            {m}
          </span>
        ))}
      </div>
      <span className="conn-desc">
        Di partenza solo GET: le scritture si accendono una per una nella scheda.
      </span>
    </div>
  );
}
